import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  AlertCircle,
  CheckCircle,
  CreditCard,
  Download,
  Eye,
  MoreHorizontal,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { ListTTReviews } from '@cold-ambulance/client';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Label,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Textarea,
} from '@cold-ambulance/shadcn';
import {
  TableRowSort,
  TheButton,
  ViewStoredFile,
  client,
  formatDate,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import {
  StickyTableBox,
  StickyTableHeader,
} from '../../components/sticky-table-box.tsx';

export default function TTReviewsPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns: ColumnDef<ListTTReviews['records'][number]>[] = [
    {
      accessorKey: 'request.customer.name',
      header: 'Customer',
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.request.customer.name}
        </span>
      ),
    },
    {
      accessorKey: 'vendor.name',
      header: 'Vendor',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.vendor.name}</span>
      ),
    },
    {
      accessorKey: 'request.projectName',
      header: 'Project',
      cell: ({ row }) => (
        <span className="max-w-[200px] truncate">
          {row.original.request.projectName}
        </span>
      ),
    },
    {
      accessorKey: 'ttLabel',
      header: 'TT Type',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.ttLabel}
        </span>
      ),
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.price.toLocaleString()} SAR
        </span>
      ),
    },
    {
      accessorKey: 'ttUploadedAt',
      header: 'TT Uploaded',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.ttUploadedAt
            ? formatDate(row.original.ttUploadedAt)
            : '-'}
        </span>
      ),
    },
    {
      accessorKey: 'ttDeadline',
      header: 'Deadline',
      cell: ({ row }) => {
        if (!row.original.ttDeadline) return '-';
        const deadline = new Date(row.original.ttDeadline);
        const isOverdue = deadline < new Date();
        return (
          <span
            className={`text-sm ${isOverdue ? 'font-medium text-red-600' : 'text-muted-foreground'}`}
          >
            {formatDate(row.original.ttDeadline)}
            {isOverdue && ' (overdue)'}
          </span>
        );
      },
    },
    {
      id: 'rejectionStatus',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => {
        const ttStatus = row.original.ttStatus;
        if (ttStatus === 'APPROVED') {
          return (
            <Badge variant="outline" className="gap-1">
              <CheckCircle className="h-3 w-3" />
              Approved
            </Badge>
          );
        }
        if (ttStatus === 'REJECTED') {
          return (
            <Badge variant="destructive" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              Rejected
            </Badge>
          );
        }
        if (row.original.ttRejectionReason) {
          return (
            <Badge variant="secondary" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              Re-submission
            </Badge>
          );
        }
        return (
          <Badge variant="outline" className="gap-1">
            <CreditCard className="h-3 w-3" />
            Pending Review
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const offer = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <ReviewTTDialog
                offer={offer}
                onAction={() => dataQuery.refetch()}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <Eye className="mr-2 h-4 w-4" />
                    مراجعة إثبات رسوم المنصة
                  </DropdownMenuItem>
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const dataQuery = useData(
    'GET /admin/tt-reviews',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sorting.length > 0 ? (sorting[0].id as 'createdAt') : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="مراجعات إثبات الدفع - رسوم المنصة (CA TT)"
        totalCount={`${dataQuery.data?.totalCount || 0} total reviews`}
        icon={CreditCard}
      />

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No pending TT reviews.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}

function ReviewTTDialog({
  offer,
  onAction,
  trigger,
}: {
  offer: ListTTReviews['records'][number];
  onAction: () => void;
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const approveMutation = useAction(
    'POST /admin/tt-reviews/{offerId}/approve',
    {
      meta: { toast: true },
      onSuccess: () => {
        setOpen(false);
        onAction();
        toast.success('Payment proof approved');
      },
    },
  );

  const rejectMutation = useAction('POST /admin/tt-reviews/{offerId}/reject', {
    meta: { toast: true },
    onSuccess: () => {
      setOpen(false);
      setRejectionReason('');
      onAction();
      toast.success('Payment proof rejected');
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>مراجعة إثبات تحويل رسوم المنصة (CA TT)</DialogTitle>
          <DialogDescription>
            هذا هو إثبات الدفع الخاص برسوم منصة MedHub فقط (10%). المورد يتولى
            مراجعة إثبات الدفع الخاص به بشكل مستقل.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 rounded-lg border p-4">
            <div>
              <p className="text-muted-foreground text-sm">Customer</p>
              <p className="font-medium">{offer.request.customer.name}</p>
              <p className="text-muted-foreground text-sm">
                {offer.request.customer.email}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Vendor</p>
              <p className="font-medium">{offer.vendor.name}</p>
              <p className="text-muted-foreground text-sm">
                {offer.vendor.email}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Project</p>
              <p className="font-medium">{offer.request.projectName}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Price</p>
              <p className="font-medium">{offer.price.toLocaleString()} SAR</p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">TT Uploaded</p>
              <p className="font-medium">
                {offer.ttUploadedAt ? formatDate(offer.ttUploadedAt) : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Deadline</p>
              <p className="font-medium">
                {offer.ttDeadline ? formatDate(offer.ttDeadline) : '-'}
              </p>
            </div>
          </div>

          {/* Previous rejection reason */}
          {offer.ttRejectionReason && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    Previously Rejected
                  </p>
                  <p className="text-sm text-amber-700">
                    {offer.ttRejectionReason}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TT File */}
          <div>
            <h3 className="mb-3 font-medium">
              إثبات تحويل رسوم المنصة ({offer.ttLabel})
            </h3>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="font-medium">إثبات تحويل رسوم المنصة (CA)</p>
                <p className="text-muted-foreground text-sm">
                  Uploaded{' '}
                  {offer.ttUploadedAt && formatDate(offer.ttUploadedAt)}
                </p>
              </div>
              {offer.ttFileId && <ViewStoredFile fileId={offer.ttFileId} />}
            </div>
          </div>

          {offer.docusealSubmissionId && (
            <div>
              <h3 className="mb-3 font-medium">Signed contract</h3>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <p className="text-muted-foreground text-sm">
                  Vendor–customer service agreement signed via DocuSeal
                </p>
                <DownloadSignedContract
                  submissionId={offer.docusealSubmissionId}
                />
              </div>
            </div>
          )}

          {/* Rejection reason input */}
          <div className="space-y-2">
            <Label htmlFor="rejectionReason">
              Rejection Reason (required for rejection)
            </Label>
            <Textarea
              id="rejectionReason"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              disabled={offer.ttStatus === 'APPROVED'}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 space-x-2 sm:gap-0">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <TheButton
            variant="destructive"
            loading={rejectMutation.isPending}
            icon={<XCircle className="h-4 w-4" />}
            disabled={
              offer.ttStatus === 'APPROVED' ||
              !rejectionReason.trim() ||
              approveMutation.isPending
            }
            onClick={() => {
              rejectMutation.mutate({
                offerId: offer.id,
                reason: rejectionReason.trim(),
              });
            }}
          >
            Reject
          </TheButton>
          <TheButton
            loading={approveMutation.isPending}
            icon={<CheckCircle className="h-4 w-4" />}
            disabled={offer.ttStatus === 'APPROVED' || rejectMutation.isPending}
            onClick={() => {
              approveMutation.mutate({ offerId: offer.id });
            }}
          >
            Approve
          </TheButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DownloadSignedContract({ submissionId }: { submissionId: string }) {
  const [isLoading, setIsLoading] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1"
      disabled={isLoading}
      onClick={async () => {
        setIsLoading(true);
        const popup = window.open('about:blank', '_blank');
        try {
          const { combinedDocumentUrl } = await client.request(
            'GET /admin/signing/{submissionId}/document',
            { submissionId },
          );
          if (!combinedDocumentUrl) {
            popup?.close();
            toast.error(
              'Signed document is still being generated. Try again in a moment.',
            );
            return;
          }
          if (popup) {
            popup.opener = null;
            popup.location.replace(combinedDocumentUrl);
          } else {
            window.open(combinedDocumentUrl, '_blank', 'noopener');
          }
        } catch (error) {
          popup?.close();
          toast.error(
            error instanceof Error
              ? error.message
              : 'Failed to open signed document',
          );
        } finally {
          setIsLoading(false);
        }
      }}
    >
      <Download className="h-3.5 w-3.5" />
      {isLoading ? 'Loading...' : 'Download'}
    </Button>
  );
}
